import ConnectionModel from '@mm/state/model/ConnectionModel';
import { useSelector } from 'react-redux';
import {
  selectGoogleAccounts,
  selectSheetColumns,
  selectSheets,
  selectSpreadsheets,
} from '@mm/state/selectors/googleSheetSelectors';
import { selectConnectionsGoogleAccounts } from '@mm/state/modules/connectionsTableState/selectors';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  GoogleAccount,
  GoogleSpreadsheet,
} from '@mm/state/interfaces/googleSheet';
import AttributeModel from '@mm/state/model/AttributeModel';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { selectAttributes } from '@mm/state/selectors/attributeSelectors';
import { QuestionCircleIcon, SyncIcon } from '@mm/icons';
import { Heading } from '@mm/components';
import { ReactComponent as ArrowRightLongIcon } from 'assets/icons/arrow-right-long.svg';
import { unionBy } from 'lodash';
import React, { MouseEvent, useEffect, useState } from 'react';
import PrimarySelect, {
  DropdownOption,
} from '../../../../../../../../../common/components/PrimarySelect/PrimarySelect';
import {
  blankVariable,
  canBeTested,
} from '../../../../../../../connections/panels/EditConnectionPanel/helpers';
import {
  IGNORELIST,
  PHONE_ATTRIBUTE_NAME,
  phoneAttribute,
} from '../../../../../../../../../common/modals/ImportContantListModal/ImportContantListModal';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PrimaryToolTip } from '../../../../../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { colors } from '../../../../../../styles/colors';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { IndexAttributeNameMap } from '@mm/state/modules/modalsState/props';

type GoogleSheet = {
  id: number;
  title: string;
};

export const useEditGoogleSheet = (
  setConnection: (connection: ConnectionModel) => void,
  connection?: ConnectionModel,
  usedInSlideout?: boolean,
) => {
  const accounts = useSelector(selectGoogleAccounts);
  const connectionsGoogleAccounts = useSelector(
    selectConnectionsGoogleAccounts,
  );
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);
  const chatbotId = useSelector(activeChatbotIdSelector);
  const { update } = useConnectionCRUDActions();
  const { openSpreadsheetTestConnectionModal } = useModalActions();
  const allAccounts = unionBy(connectionsGoogleAccounts, accounts, 'id');
  const filtertedAccounts = accounts.filter(acc => !acc.readOnly);
  const spreadsheets = useSelector(selectSpreadsheets);
  const sheets = useSelector(selectSheets);
  const [selectedAccount, setSelectedAccount] = useState<GoogleAccount>();
  const [selectedSpreadsheet, setSelectedSpreadsheet] =
    useState<GoogleSpreadsheet>();
  const [selectedSheet, setSelectedSheet] = useState<GoogleSheet>();
  const [selectedLookupColumn, setSelectedLookupColumn] =
    useState<DropdownOption>();
  const [selectedLookupValue, setSelectedLookupValue] =
    useState<AttributeModel>();
  const { getSpreadsheets, getSheets, getColumns } =
    useGoogleSheetsCRUDActions();
  const columns = useSelector(selectSheetColumns);
  const isReadOnlyAccount = !!accounts.find(
    acc => acc.id === connection?.googleAccountId && acc.readOnly,
  );
  const mapFiltered = columns.map(column => {
    const variableColumn = connection?.variables?.find(
      variable => variable.sentAs === column.name,
    );
    if (variableColumn) {
      return variableColumn;
    }
    return {
      sentAs: column.name,
      name: '',
    };
  });
  const mappedVars = connection?.variables?.map(variable => {
    return {
      sentAs: variable.sentAs,
      name: variable.name,
    };
  });
  const mappings =
    mapFiltered.length && !isReadOnlyAccount ? mapFiltered : mappedVars;

  const isMyAccount = !!(
    connection?.googleAccountId &&
    connection?.googleAccountEmail &&
    accounts.find(
      account =>
        account.id === connection.googleAccountId &&
        account.email === connection.googleAccountEmail,
    )
  );

  const googleSpreadsheetTitle = connection?.googleSpreadsheetTitle ?? '';
  const googleSheetTitle = connection?.googleSheetTitle ?? '';

  useEffect(() => {
    if (connection?.googleAccountId) {
      const currAccount = allAccounts.find(
        account => account.id === connection.googleAccountId,
      );
      if (currAccount?.id && currAccount?.email) {
        setSelectedAccount(currAccount as GoogleAccount);
      }
    }
    if (isMyAccount) {
      if (connection?.googleSpreadsheetId) {
        setSelectedSpreadsheet(
          spreadsheets.find(
            spreadsheet => spreadsheet.id === connection.googleSpreadsheetId,
          ),
        );
      } else {
        setSelectedSpreadsheet(undefined);
      }

      if (connection?.googleSheetId) {
        setSelectedSheet(
          sheets.find(sheet => sheet.id === Number(connection.googleSheetId)),
        );
      } else {
        setSelectedSheet(undefined);
      }
    }
    if (connection?.googleLookupColumn) {
      if (columns) {
        setSelectedLookupColumn(
          columns.find(column => column.id === connection.googleLookupColumn),
        );
      }
      setSelectedLookupColumn({
        id: connection.googleLookupColumn,
        name: connection.googleLookupColumn,
      });

      setSelectedLookupValue(
        connection.variables?.find(
          variable => variable.sentAs === connection.googleLookupColumn,
        ),
      );
    } else {
      setSelectedLookupColumn(undefined);
      setSelectedLookupValue(undefined);
    }
  }, [connection, sheets, spreadsheets]);

  const updateConnection = async (updatedFields: Partial<ConnectionModel>) => {
    if (!connection) {
      return;
    }
    const updatedConnection = await update(activeFbPageId, {
      ...connection,
      validationMessages: null,
      ...updatedFields,
    });

    if (updatedConnection) {
      setConnection(updatedConnection);
    }
  };

  const getVariablesToUpdate = (name: string, sentAs: string) => {
    let idx = 0;
    if (connection?.variables) {
      idx = connection.variables.findIndex(
        variable => variable.sentAs === sentAs || variable.name === name,
      );
      return [
        ...connection.variables.slice(0, idx),
        {
          ...blankVariable(),
          name,
          sentAs,
        },
        ...connection.variables.slice(idx + 1),
      ];
    }
  };
  const updateVariables = (name: string, sentAs: string) => {
    updateConnection({
      variables: getVariablesToUpdate(name, sentAs),
    });
  };

  const onSelectAccount = (item: GoogleAccount) => {
    setSelectedAccount(item);
    updateConnection({
      googleAccountId: item.id,
      googleAccountEmail: item.email,
      googleSpreadsheetId: null,
      googleSpreadsheetTitle: null,
      googleSheetId: null,
      googleSheetTitle: null,
      variables: [],
      googleLookupColumn: '',
    });
    getSpreadsheets(item.id);
  };

  const onSelectSpreadsheet = (item: GoogleSpreadsheet) => {
    setSelectedSpreadsheet(item);
    updateConnection({
      googleSpreadsheetId: item.id,
      googleSpreadsheetTitle: item.name,
      googleSheetId: null,
      googleSheetTitle: null,
      variables: [],
      googleLookupColumn: '',
    });
    if (selectedAccount) {
      getSheets(selectedAccount.id, item.id);
    }
  };

  const onSelectSheet = async (item: GoogleSheet) => {
    setSelectedSheet(item);
    await updateConnection({
      googleSheetId: String(item.id),
      googleSheetTitle: item.title,
      variables: [],
      googleLookupColumn: '',
    });
    if (connection) {
      await getColumns(chatbotId, connection.id);
    }
  };

  const onSelectLookupColumn = (item: DropdownOption) => {
    const variable = connection?.variables?.find(
      variable => variable.sentAs === selectedLookupColumn?.name,
    );
    if (variable && item.name) {
      updateConnection({
        googleLookupColumn: item.name,
        variables: getVariablesToUpdate(variable.name, item.name),
      });
    } else {
      updateConnection({
        googleLookupColumn: item.name,
      });
    }
    setSelectedLookupColumn(item);
  };

  const onSelectLookupValue = (item: AttributeModel) => {
    setSelectedLookupValue(item);
    if (selectedLookupColumn?.name) {
      updateVariables(item.name, selectedLookupColumn.name);
    }
  };

  const onClickActivate = () => {
    updateConnection({
      active: true,
    });
  };

  const onClickTestConnection = () => {
    if (connection) {
      openSpreadsheetTestConnectionModal({ connection, onClickActivate });
    }
  };
  const onClickRefresh = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (connection) {
      getColumns(chatbotId, connection.id);
    }
  };

  const whatToDoOptions: DropdownOption[] = [
    {
      id: 1,
      name: 'Update existing or create a new spreadsheet row',
    },
  ];
  const [whatToDoOptionSelected, setWhatToDoOptionSelected] = useState(
    whatToDoOptions[0],
  );

  const whenBeTriggeredOptions: DropdownOption[] = [
    {
      id: 1,
      name: 'Update when a connection widget is triggered',
    },
  ];
  const [whenBeTriggeredOptionSelected, setWhenBeTriggeredOptionSelected] =
    useState(whenBeTriggeredOptions[0]);

  const [map, setMap] = useState<IndexAttributeNameMap>({});
  const attributes = useSelector(selectAttributes);

  const withPhone = attributes.some(
    ({ name }) => name === PHONE_ATTRIBUTE_NAME,
  );
  const attrs = !withPhone ? [...attributes, phoneAttribute] : attributes;

  const selectedNames = Object.values(map);
  const options = attrs
    .filter(attr => !selectedNames.includes(attr.name))
    .filter(attr => !IGNORELIST.includes(attr.name))
    .filter(attr => attr.name !== selectedLookupValue?.name);

  const onAttributeSelected = (
    { name, system }: AttributeModel,
    sentAs: string,
    index: number,
  ) => {
    const next = { ...map, [index]: name };
    setMap(next);

    updateVariables(name, sentAs);
  };

  const disabled = !connection ? false : !canBeTested(connection, true);

  const configs = [
    {
      header: 'Connection Settings',
      children: (
        <div className="edit-google-sheet-item__content">
          <div className="edit-google-sheet-item__section">
            <Heading
              className="edit-google-sheet-item__section-name"
              size={5}
              text="What would you like to do?"
            />
            <PrimarySelect
              className="edit-google-sheet-item__dropdown"
              options={whatToDoOptions}
              value={whatToDoOptionSelected}
              onChange={item => setWhatToDoOptionSelected(item)}
              dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              customDropdownStyles={{ zIndex: '1000003' }}
            />
          </div>
          <div className="edit-google-sheet-item__section">
            <Heading
              className="edit-google-sheet-item__section-name"
              size={5}
              text="When will the integration be triggered?"
            />
            <PrimarySelect
              className="edit-google-sheet-item__dropdown"
              options={whenBeTriggeredOptions}
              value={whenBeTriggeredOptionSelected}
              onChange={item => setWhenBeTriggeredOptionSelected(item)}
              dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              customDropdownStyles={{ zIndex: '1000003' }}
            />
          </div>
          <div className="edit-google-sheet-item__section">
            <Heading
              size={5}
              className="edit-google-sheet-item__set-up-section-header"
              text="Set up your worksheet"
            />

            <div className="edit-google-sheet-item__select-container">
              <div className="edit-google-sheet-item__account-dropdown-container">
                <Heading
                  size={5}
                  className="edit-google-sheet-item__set-up-worksheet"
                  text="Choose an account"
                />
                <PrimarySelect
                  className="edit-google-sheet-item__dropdown"
                  placeholder="Choose an account"
                  options={filtertedAccounts}
                  value={selectedAccount}
                  onChange={item => onSelectAccount(item as GoogleAccount)}
                  displayNameKey="email"
                  dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
                  customDropdownStyles={{ zIndex: '1000003' }}
                  disabled={isReadOnlyAccount}
                />
              </div>
            </div>
            <Heading
              size={5}
              className="edit-google-sheet-item__set-up-worksheet"
              text=" Choose Spreadsheet"
            />
            <PrimarySelect
              className="edit-google-sheet-item__dropdown"
              placeholder={
                googleSpreadsheetTitle
                  ? googleSpreadsheetTitle
                  : 'Choose Spreadsheet'
              }
              options={spreadsheets}
              value={selectedSpreadsheet}
              onChange={item => onSelectSpreadsheet(item as GoogleSpreadsheet)}
              dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              disabled={isReadOnlyAccount}
              customDropdownStyles={{ zIndex: '1000003' }}
            />
            <Heading
              size={5}
              className="edit-google-sheet-item__set-up-worksheet"
              text="Choose a Worksheet"
            />
            <PrimarySelect
              className="edit-google-sheet-item__dropdown"
              placeholder={
                googleSheetTitle ? googleSheetTitle : 'Choose a Worksheet'
              }
              displayNameKey="title"
              options={sheets}
              value={selectedSheet}
              onChange={item => onSelectSheet(item as GoogleSheet)}
              dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              disabled={isReadOnlyAccount}
              customDropdownStyles={{ zIndex: '1000003' }}
            />
          </div>
        </div>
      ),
    },
    {
      header: 'Look up an existing row',
      children: (
        <div className="edit-google-sheet-item__content">
          <div className="edit-google-sheet-item__section">
            <Heading size={5} text="Choose lookup column" />
            <div className="edit-google-sheet-item__description">
              Search for an existing record using the values in this column
              before writing a new row
            </div>
            <div className="edit-google-sheet-item__column-select-container">
              <PrimarySelect
                className="edit-google-sheet-item__dropdown"
                placeholder="Choose a column"
                options={columns}
                value={selectedLookupColumn}
                onChange={onSelectLookupColumn}
                dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
                customDropdownStyles={{ zIndex: '1000003' }}
                disabled={isReadOnlyAccount}
              />
            </div>
            <PrimaryButton
              color={ButtonColor.secondaryBlue}
              className="edit-google-sheet-item__refresh-button"
              onClick={onClickRefresh}
              leftIcon={<SyncIcon color="#2f54eb" />}
            >
              Refresh
            </PrimaryButton>
          </div>
          <div className="edit-google-sheet-item__section">
            <Heading size={5} text="Insert Lookup value" />
            <div className="edit-google-sheet-item__description">
              Search for the following value in the column above
            </div>
            <PrimarySelect
              className="edit-google-sheet-item__dropdown"
              dropdownClassName="edit-google-sheet-item__dropdown-wrapper"
              placeholder="Choose a value"
              options={options}
              value={selectedLookupValue}
              onChange={v => onSelectLookupValue(v as AttributeModel)}
              showSearchFilter={true}
              customOptionsPosition="top"
              customDropdownStyles={{ zIndex: '1000003' }}
            />
          </div>
        </div>
      ),
    },
    {
      header: (
        <div className="edit-google-sheet-item__menu">
          Update row or create new row with attribute values
          <PrimaryButton
            color={ButtonColor.secondaryBlue}
            className="edit-google-sheet-item__refresh-button"
            onClick={onClickRefresh}
            leftIcon={<SyncIcon />}
          >
            Refresh
          </PrimaryButton>
          <PrimaryToolTip
            align="center"
            position="bottom"
            anchorElement={<QuestionCircleIcon color={colors.accentBlue} />}
            useHover
          >
            Choose which attributes to populate under each column in your
            spreadsheet
          </PrimaryToolTip>
        </div>
      ),
      children: (
        <div className="edit-google-sheet-item__content">
          {mappings?.length ? (
            <div className="edit-google-sheet-item__mapper-container">
              <div className="edit-google-sheet-item__mapper-header">
                <div className="edit-google-sheet-item__mapper-header-title">
                  Google Sheets column
                </div>
                <div className="edit-google-sheet-item__mapper-header-title">
                  MobileMonkey attribute
                </div>
              </div>
              <div className="edit-google-sheet-item__mapper-rows">
                {mappings?.map((column, index: number) => (
                  <div
                    className="edit-google-sheet-item__mapper-row"
                    key={`${column.name}-${index}`}
                  >
                    <div className="edit-google-sheet-item__mapper-column-name">
                      {column.sentAs}
                    </div>
                    <span className="edit-google-sheet-item__mapper-arrow">
                      <ArrowRightLongIcon />
                    </span>
                    <div className="edit-google-sheet-item__mapper-attribute">
                      <PrimarySelect
                        className="edit-google-sheet-item__mapper-attribute-selector"
                        dropdownClassName="edit-google-sheet-item__mapper-attribute-selector__dropdown"
                        options={options}
                        value={attrs.find(({ name }) => name === column.name)}
                        onChange={v =>
                          onAttributeSelected(
                            v as AttributeModel,
                            column.sentAs ?? '',
                            index + 1,
                          )
                        }
                        showSearchFilter
                        customOptionsPosition="top"
                        customDropdownStyles={{ zIndex: '1000003' }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="edit-google-sheet-item__warning">
              <ExclamationCircleFilled className="edit-google-sheet-item__exclamation-filled-icon" />
              Please fill in previous steps to continue
            </div>
          )}
          {!usedInSlideout && mappings?.length && (
            <div className="edit-google-sheet-item__mapper-actions">
              <PrimaryButton
                color={ButtonColor.blue}
                className="edit-google-sheet-item__mapper-activate-button"
                onClick={onClickActivate}
                disabled={connection?.active || disabled}
              >
                Activate
              </PrimaryButton>
              <PrimaryButton
                color={ButtonColor.secondaryBlue}
                onClick={onClickTestConnection}
                disabled={disabled}
              >
                Test Connection
              </PrimaryButton>
            </div>
          )}
        </div>
      ),
    },
  ];

  return { configs };
};
